type RtpSenderWithKeyFrame = RTCRtpSender & {
  generateKeyFrame?: (rids?: string[]) => Promise<void>
}

type CanvasCaptureTrack = MediaStreamTrack & {
  requestFrame?: () => void
}

export type RequestKeyframeOptions = {
  /**
   * Fallback for real getUserMedia tracks when generateKeyFrame / same-track rebind fail.
   * Must stay false for canvas.captureStream / blur tracks — toggling enabled stalls SFU ~30s.
   */
  allowEnabledNudge?: boolean
  /** Extra generateKeyFrame / rebind attempt after a short settle. */
  retryMs?: number
}

const isCanvasCaptureTrack = (track: MediaStreamTrack): boolean => {
  // getUserMedia / display tracks expose deviceId or displaySurface; canvas.captureStream does not.
  const settings = track.getSettings?.() as MediaTrackSettings & { displaySurface?: string }
  return !settings?.deviceId && !settings?.displaySurface
}

const nudgeCanvasFrame = (track: MediaStreamTrack | null | undefined) => {
  const canvasTrack = track as CanvasCaptureTrack | null | undefined
  canvasTrack?.requestFrame?.()
}

const tryGenerateKeyFrame = async (sender: RTCRtpSender): Promise<boolean> => {
  const withKeyFrame = sender as RtpSenderWithKeyFrame
  if (typeof withKeyFrame.generateKeyFrame !== 'function') {
    return false
  }
  try {
    await withKeyFrame.generateKeyFrame()
    return true
  } catch {
    return false
  }
}

/**
 * Re-bind the same track. Many Chromium SFU setups emit an IDR without generateKeyFrame
 * and without toggling track.enabled (safe for canvas/blur).
 */
const tryReplaceSameTrack = async (sender: RTCRtpSender): Promise<boolean> => {
  const track = sender.track
  if (!track || track.readyState !== 'live') return false
  try {
    await sender.replaceTrack(track)
    return true
  } catch {
    return false
  }
}

const delay = (ms: number) => new Promise<void>((resolve) => {
  window.setTimeout(resolve, ms)
})

/**
 * After replaceTrack (esp. canvas ↔ camera in SFU) remotes freeze on the last frame
 * until a keyframe arrives. Force one when the API exists.
 *
 * Never toggle `enabled` on canvas.captureStream tracks — that stalls SFU encoding ~30s.
 */
export const requestKeyframeAfterReplace = async (
  sender: RTCRtpSender,
  options: RequestKeyframeOptions = {},
): Promise<void> => {
  const allowEnabledNudge = options.allowEnabledNudge !== false
  const retryMs = options.retryMs ?? 0

  nudgeCanvasFrame(sender.track)

  if (await tryGenerateKeyFrame(sender)) {
    if (retryMs > 0) {
      await delay(retryMs)
      nudgeCanvasFrame(sender.track)
      await tryGenerateKeyFrame(sender)
    }
    return
  }

  if (retryMs > 0) {
    await delay(retryMs)
    nudgeCanvasFrame(sender.track)
    if (await tryGenerateKeyFrame(sender)) return
  }

  // Preferred fallback for blur/canvas and for browsers without generateKeyFrame.
  if (await tryReplaceSameTrack(sender)) {
    if (retryMs > 0) {
      await delay(retryMs)
      nudgeCanvasFrame(sender.track)
      await tryReplaceSameTrack(sender)
    }
    return
  }

  const track = sender.track
  if (!allowEnabledNudge || !track || track.readyState !== 'live' || isCanvasCaptureTrack(track)) {
    return
  }

  track.enabled = false
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve())
  })
  track.enabled = true
}

/**
 * SFU remotes often need several IDR attempts after camera↔canvas swap.
 * Fire-and-forget bursts; safe while blur canvas encoder is still warming up.
 */
export const scheduleKeyframeBurstAfterReplace = (
  sender: RTCRtpSender,
  options: RequestKeyframeOptions & { delaysMs?: number[] } = {},
): void => {
  const delaysMs = options.delaysMs ?? [0, 400, 1000, 2000]
  const allowEnabledNudge = options.allowEnabledNudge ?? false
  const retryMs = options.retryMs ?? 0

  delaysMs.forEach((ms) => {
    window.setTimeout(() => {
      if (!sender.track || sender.track.readyState !== 'live') return
      void requestKeyframeAfterReplace(sender, {
        allowEnabledNudge,
        retryMs,
      })
    }, ms)
  })
}
